// ServiceGroupDialog.tsx
import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "src/context/auth";
import { useNotifications } from "@toolpad/core/useNotifications";
import { getRoutes } from "src/utilities/routes";
import LoadingButton from "@mui/lab/LoadingButton";

import {
  DataGrid,
  GridColDef,
  GridRowId,
  GridRowModes,
  GridRowModesModel,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import { handleSendMessage } from "src/utilities";
import { IServiceGroup } from "src/interface/IServiceGroup";

const validationSchema = Yup.object({
  name: Yup.string().required("نام گروه الزامی است"),
  description: Yup.string().nullable(),
});

const initialValues = {
  name: "",
  description: "",
  id: undefined,
};

const ServiceGroupDialog = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const { user } = useAuth();
  const notifications = useNotifications();
  const Routes = getRoutes();
  const [rows, setRows] = useState<IServiceGroup[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const { mutate: getServiceGroups, isPending: isGroupsLoading } = useMutation({
    mutationFn: async () => {
      const response = await fetch(Routes.ServiceGroupsRoutes, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      });
      return await response.json();
    },
    onSuccess: (responseData: IServiceGroup[]) => {
      setRows(responseData);
    },
    onError: (error) => {
      notifications.show("خطا در دریافت گروه‌ها", {
        severity: "error",
        autoHideDuration: 3000,
      });
      console.log(error);
    },
  });

  const { mutateAsync: createServiceGroup, isPending: isCreating } =
    useMutation({
      mutationFn: async (body: any) => {
        const response = await fetch(Routes.ServiceGroupsRoutes, {
          method: "POST",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json-patch+json",
            Authorization: `Bearer ${user?.token}`,
          },
          body: JSON.stringify(body),
        });
        return await response.json();
      },
      onSuccess: () => {
        handleSendMessage(notifications, "گروه با موفقیت ایجاد شد!", "success");
        getServiceGroups();
      },
      onError: (error) => {
        handleSendMessage(notifications, "خطا در ایجاد گروه!", "error");
        console.log(error);
      },
    });

  const { mutateAsync: updateServiceGroup, isPending: isUpdating } =
    useMutation({
      mutationFn: async (body: any) => {
        const response = await fetch(
          `${Routes.ServiceGroupsRoutes}${body.id}`,
          {
            method: "PUT",
            headers: {
              Accept: "*/*",
              "Content-Type": "application/json-patch+json",
              Authorization: `Bearer ${user?.token}`,
            },
            body: JSON.stringify(body),
          }
        );
        return await response.json();
      },
      onSuccess: () => {
        handleSendMessage(
          notifications,
          "گروه با موفقیت ویرایش شد!",
          "success"
        );
        getServiceGroups();
      },
      onError: (error) => {
        handleSendMessage(notifications, "خطا در ویرایش گروه!", "error");
        console.log(error);
      },
    });

  const { mutateAsync: deleteServiceGroup, isPending: isDeleting } =
    useMutation({
      mutationFn: async (id: number) => {
        const response = await fetch(`${Routes.ServiceGroupsRoutes}${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Accept: "application/json",
          },
        });
        return await response.json();
      },
      onSuccess: () => {
        handleSendMessage(notifications, "گروه با موفقیت حذف شد!", "success");
        getServiceGroups();
      },
      onError: (error) => {
        handleSendMessage(notifications, "خطا در حذف گروه!", "error");
        console.log(error);
      },
    });

  const formik = useFormik<IServiceGroup>({
    initialValues,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (values.id) {
        await updateServiceGroup({ ...values });
      } else {
        await createServiceGroup({ ...values });
      }
      resetForm();
    },
  });

  useEffect(() => {
    if (open) {
      getServiceGroups();
    }
  }, [open]);

  const handleEditClick = (id: GridRowId) => () => {
    const group = rows.find((row) => row.id === id);
    if (group) {
      formik.setValues({
        id: group.id,
        name: group.name,
        description: group.description || "",
      });
      setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    }
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    deleteServiceGroup(id as number);
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
    formik.resetForm();
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "نام گروه",
      minWidth: 150,
      editable: true,
    },
    {
      field: "description",
      headerName: "توضیحات",
      minWidth: 200,
      editable: true,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "عملیات",
      minWidth: 150,
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="ذخیره"
              onClick={handleSaveClick(id)}
              color="primary"
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="انصراف"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="ویرایش"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="حذف"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
        formik.resetForm();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>مدیریت گروه‌های خدمات</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label="نام گروه"
            {...formik.getFieldProps("name")}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            margin="normal"
          />
          <TextField
            fullWidth
            label="توضیحات"
            {...formik.getFieldProps("description")}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={formik.touched.description && formik.errors.description}
            margin="normal"
          />
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            disabled={isCreating || isUpdating}
            loading={isCreating || isUpdating}
            sx={{ mt: 2 }}
          >
            {formik.values.id ? "ویرایش" : "ایجاد"}
          </LoadingButton>
        </Box>
        <Box sx={{ height: 400, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={isGroupsLoading}
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            processRowUpdate={(newRow) => {
              updateServiceGroup(newRow);
              return newRow;
            }}
            editMode="row"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="error">
          انصراف
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceGroupDialog;
